import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

/**
 * Custom hook for managing URL state in a DRY way
 * Automatically syncs state with URL query parameters
 * Preserves all state on page reload
 * 
 * @param {Object} defaultValues - Default values for URL parameters
 * @returns {[Object, Function]} - [urlState, setUrlState]
 */
export const useUrlState = (defaultValues = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Parse current URL state
  const urlState = useMemo(() => {
    const state = {};
    
    // Get all params from URL
    for (const [key, value] of searchParams.entries()) {
      // Try to parse JSON for complex values
      try {
        state[key] = JSON.parse(value);
      } catch {
        // If not JSON, use as string
        state[key] = value;
      }
    }
    
    // Apply defaults for missing values
    for (const [key, defaultValue] of Object.entries(defaultValues)) {
      if (!(key in state)) {
        state[key] = defaultValue;
      }
    }
    
    
    return state;
  }, [searchParams, defaultValues]);

  // Simple update function with change detection
  const setUrlState = useCallback((updates, options = {}) => {
    const { replace = false, keepExisting = true } = options;
    
    // Handle function updates by recalculating current state
    let finalUpdates = updates;
    if (typeof updates === 'function') {
      const currentState = {};
      // Parse current URL state
      for (const [key, value] of searchParams.entries()) {
        try {
          currentState[key] = JSON.parse(value);
        } catch {
          currentState[key] = value;
        }
      }
      // Apply defaults
      for (const [key, defaultValue] of Object.entries(defaultValues)) {
        if (!(key in currentState)) {
          currentState[key] = defaultValue;
        }
      }
      finalUpdates = updates(currentState);
    }
    
    const newParams = new URLSearchParams();
    
    // Keep existing params if requested
    if (keepExisting) {
      for (const [key, value] of searchParams.entries()) {
        if (!(key in finalUpdates)) {
          newParams.set(key, value);
        }
      }
    }
    
    // Add updated params
    for (const [key, value] of Object.entries(finalUpdates)) {
      if (value === null || value === undefined) {
        // Remove param if null/undefined
        newParams.delete(key);
      } else if (typeof value === 'object') {
        // Stringify objects/arrays
        newParams.set(key, JSON.stringify(value));
      } else {
        newParams.set(key, String(value));
      }
    }
    
    // Only update URL if params actually changed
    const currentParamString = searchParams.toString();
    const newParamString = newParams.toString();
    
    if (currentParamString !== newParamString) {
      setSearchParams(newParams, { replace });
    }
  }, [searchParams, setSearchParams, defaultValues]);

  return [urlState, setUrlState];
};

/**
 * Hook for managing pagination state in URL
 */
export const useUrlPagination = (defaultPage = 1, defaultLimit = 50) => {
  const [urlState, setUrlState] = useUrlState({
    page: defaultPage,
    limit: defaultLimit
  });
  
  const setPage = useCallback((page) => {
    setUrlState({ page });
  }, [setUrlState]);
  
  const setLimit = useCallback((limit) => {
    setUrlState({ limit, page: 1 }); // Reset to page 1 when changing limit
  }, [setUrlState]);
  
  return {
    page: Number(urlState.page),
    limit: Number(urlState.limit),
    setPage,
    setLimit
  };
};

/**
 * Hook for managing filter state in URL
 */
export const useUrlFilters = (defaultFilters = {}) => {
  const [urlState, setUrlState] = useUrlState({
    filters: defaultFilters
  });
  
  const setFilter = useCallback((key, value) => {
    setUrlState(current => ({
      filters: {
        ...current.filters,
        [key]: value
      }
    }));
  }, [setUrlState]);
  
  const clearFilters = useCallback(() => {
    setUrlState({ filters: defaultFilters });
  }, [setUrlState, defaultFilters]);
  
  return {
    filters: urlState.filters || defaultFilters,
    setFilter,
    clearFilters
  };
};

/**
 * Hook for managing sort state in URL
 */
export const useUrlSort = (defaultSort = 'name', defaultOrder = 'asc') => {
  const [urlState, setUrlState] = useUrlState({
    sort: defaultSort,
    order: defaultOrder
  });
  
  const setSort = useCallback((sort, order = null) => {
    if (order === null) {
      // Toggle order if same column
      order = urlState.sort === sort && urlState.order === 'asc' ? 'desc' : 'asc';
    }
    setUrlState({ sort, order });
  }, [setUrlState, urlState.sort, urlState.order]);
  
  return {
    sort: urlState.sort,
    order: urlState.order,
    setSort
  };
};

/**
 * Combined hook for common table state (pagination, filters, sort)
 */
export const useUrlTableState = (defaults = {}) => {
  const pagination = useUrlPagination(defaults.page, defaults.limit);
  const filters = useUrlFilters(defaults.filters);
  const sort = useUrlSort(defaults.sort, defaults.order);
  
  return {
    ...pagination,
    ...filters,
    ...sort
  };
};

export default useUrlState;